import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Box, Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow,
  Switch, Chip, Button, IconButton, Tooltip, Alert, Collapse,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BuildIcon from "@mui/icons-material/Build";

import api from "../api";
import { MONO, tokens } from "../theme";
import { PageHeader } from "../components/ui";
import ClassifierModal from "../components/ClassifierModal";
import ProvisionLogModal from "../components/ProvisionLogModal";

export default function Classifiers() {
  const [rows, setRows] = useState(null);
  const [def, setDef] = useState(null);
  const [env, setEnv] = useState(null);
  const [modal, setModal] = useState(null);   // {classifier} | {create:true} | null
  const [showLog, setShowLog] = useState(false);
  const [gpuEnvOpen, setGpuEnvOpen] = useState(false);  // per-node _classify_gpu build viewer
  const logRef = useRef(null);

  const load = useCallback(() => {
    api.get("/api/classifiers", { silent: true })
      .then((d) => { setRows(d.classifiers); setDef(d.default); setEnv(d.env); })
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll the shared-env build while it's running.
  useEffect(() => {
    if (env?.status !== "building") return;
    const t = setInterval(() => {
      api.get("/api/classifiers/env", { silent: true }).then(setEnv).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
  }, [env?.status]);
  useEffect(() => {
    if (showLog && logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [env?.log, showLog]);

  const patch = async (name, body) => {
    try { await api.patch(`/api/classifiers/${name}`, body); load(); } catch { /* toasts */ }
  };
  const del = async (name) => {
    if (!window.confirm(`Delete classifier "${name}"?`)) return;
    try { await api.del(`/api/classifiers/${name}`); toast.success("Deleted"); load(); } catch { /* toasts */ }
  };
  const buildEnv = async () => {
    try {
      const e = await api.post("/api/classifiers/env/rebuild", {});
      setEnv(e); setShowLog(true);
    } catch { /* toasts */ }
  };
  const rebuildGpuEnv = async () => {
    try { await api.post("/api/classifiers/gpu-env/rebuild", {}); setGpuEnvOpen(true); }
    catch { /* toasts */ }
  };

  if (!rows) return null;

  return (
    <Box>
      <PageHeader
        eyebrow="Classify"
        title="Classifiers"
        subtitle="Zero-shot text classifiers backed by HuggingFace models, plus custom modules. Consumed over the API (POST /v1/classify) and runnable from the playground."
        actions={
          <>
            {rows.some((c) => c.device === "gpu") && (
              <Button variant="outlined" size="small" onClick={() => {
                if (window.confirm("Rebuild the GPU classifier venv (_classify_gpu) on every worker? Installs CUDA torch + transformers on each node (a few minutes per node). You can watch each node's output.")) rebuildGpuEnv();
              }}>Rebuild GPU env</Button>
            )}
            {env?.status === "ready" && (
              <Button variant="outlined" size="small" onClick={() => {
                if (window.confirm("Rebuild the shared classifier environment? Re-installs transformers + tokenizer backends (a few minutes).")) buildEnv();
              }}>Rebuild env</Button>
            )}
            <Button variant="contained" size="small" onClick={() => setModal({ create: true })}>New classifier</Button>
          </>
        }
      />

      {env && env.status !== "ready" && (
        <Alert
          severity={env.status === "error" ? "error" : env.status === "building" ? "info" : "warning"}
          icon={<BuildIcon fontSize="small" />}
          sx={{ mb: 2 }}
          action={
            env.status !== "building" && (
              <Button color="inherit" size="small" onClick={buildEnv}>Build environment</Button>
            )
          }
        >
          {env.status === "building"
            ? "Building the shared classifier environment (transformers + CPU torch)…"
            : env.status === "error"
            ? "The classifier environment failed to build."
            : "The shared classifier environment isn't built yet — zero-shot classifiers need it (the echo classifier works without it)."}
          {(env.status === "building" || env.log) && (
            <Box sx={{ mt: 0.5 }}>
              <Button size="small" color="inherit" onClick={() => setShowLog((s) => !s)} sx={{ px: 0, minWidth: 0 }}>
                {showLog ? "hide log" : "show log"}
              </Button>
              <Collapse in={showLog}>
                <Box ref={logRef} component="pre" sx={{
                  fontFamily: MONO, fontSize: ".7rem", mt: 0.5, maxHeight: 200, overflow: "auto",
                  background: "#0d1a2b", color: "#c3d2ea", borderRadius: 1, p: 1, whiteSpace: "pre-wrap",
                }}>{env.log || "…"}</Box>
              </Collapse>
            </Box>
          )}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>Device</TableCell>
                <TableCell align="center">Enabled</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.name}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 600 }}>{c.name}</span>
                      {c.name === def && <Chip size="small" label="default" sx={{ bgcolor: "rgba(46,139,230,.12)", color: tokens.signal }} />}
                      {c.source === "custom" && <Chip size="small" variant="outlined" label="custom" />}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: MONO, fontSize: ".78rem", color: "text.secondary" }}>
                    {c.model_id || "—"}
                  </TableCell>
                  <TableCell>{c.kind}</TableCell>
                  <TableCell>
                    {c.device === "gpu"
                      ? <Chip size="small" label="GPU" sx={{ height: 20, fontSize: ".7rem", bgcolor: "rgba(46,139,230,.12)", color: tokens.signal }} />
                      : <Chip size="small" variant="outlined" label="CPU" sx={{ height: 20, fontSize: ".7rem" }} />}
                  </TableCell>
                  <TableCell align="center">
                    <Switch size="small" checked={c.enabled} onChange={(e) => patch(c.name, { enabled: e.target.checked })} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setModal({ classifier: c })}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    {c.source === "custom" && (
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => del(c.name)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {modal && (
        <ClassifierModal
          classifier={modal.classifier || null}
          onClose={(changed) => { setModal(null); if (changed) load(); }}
        />
      )}

      {gpuEnvOpen && (
        <ProvisionLogModal
          endpoint="/api/classifiers/gpu-env"
          title="_classify_gpu · all nodes"
          onClose={() => setGpuEnvOpen(false)}
        />
      )}
    </Box>
  );
}
